import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { geocodeAddress } from '@/utils/geocodeAddress';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { normalizePhoneE164 } from '@/utils/phone';

export function useUpdateLeadDetails() {
  const queryClient = useQueryClient();
  const { locale } = useCompanyLocale();

  return useMutation({
    mutationFn: async ({ leadId, updates }: { leadId: string; updates: any }) => {
      if (typeof updates.customer_phone === 'string' && updates.customer_phone) {
        updates.customer_phone = normalizePhoneE164(updates.customer_phone, locale);
      }
      // If the update sets an address but no lat/lng (CSR typed freeform),
      // geocode it now so the metro trigger can assign correctly.
      if (updates.customer_address && (updates.lat == null || updates.lng == null)) {
        const coords = await geocodeAddress(updates.customer_address, locale.countryCode);
        if (coords) {
          updates.lat = coords.lat;
          updates.lng = coords.lng;
        }
      }

      const { error } = await supabase
        .from('leads')
        .update(updates)
        .eq('id', leadId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      toast({ title: 'Lead updated successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update lead',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useCancelLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, reason }: { leadId: string; reason: string }) => {
      const { error } = await supabase
        .from('leads')
        .update({ 
          status: 'lost',
          lost_reason: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      toast({ title: 'Lead cancelled successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to cancel lead',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
